import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import type { Recipient } from '@/lib/types/recipients';

/**
 * What was typed into Quick Add when "Open the full form" was clicked (#785).
 * The full form at /recipients/add starts from it, then clears it.
 */
export interface QuickAddDraft {
  firstName?: string;
  lastName?: string;
  email?: string;
  upn?: string;
  displayName?: string;
  phoneNumber?: string;
}

interface RecipientsUIState {
  // Sidepanel (view details)
  selectedRecipient: Recipient | null;
  sidepanelOpen: boolean;
  openSidepanel: (recipient: Recipient) => void;
  closeSidepanel: () => void;
  updateSelectedRecipient: (updates: Record<string, unknown>) => void;

  // Edit sidepanel
  editRecipient: Recipient | null;
  editSidepanelOpen: boolean;
  openEditSidepanel: (recipient: Recipient) => void;
  closeEditSidepanel: () => void;

  // Add modal/sidepanel
  addOptionsModalOpen: boolean;
  addSidepanelOpen: boolean;
  setAddOptionsModalOpen: (open: boolean) => void;
  setAddSidepanelOpen: (open: boolean) => void;

  // Export
  exportDialogOpen: boolean;
  selectedIdsForExport: string[];
  openExportDialog: (ids: string[]) => void;
  closeExportDialog: () => void;

  // #446 — this store is a module singleton, so panel state outlives the React
  // tree. Without this the recipients page re-mounts with `sidepanelOpen` still
  // true and re-opens the previous recipient's panel on navigating back.
  resetPanels: () => void;

  // #785 — Quick Add's "Open the full form" carries what was typed. Kept in
  // memory only (never in the address, where an email and a phone number
  // would land in the browser history), and deliberately NOT part of
  // INITIAL_PANEL_STATE: the list page calls resetPanels() as it unmounts,
  // which is exactly the moment the full form is on its way in.
  quickAddDraft: QuickAddDraft | null;
  /** When the draft was left (ms). A draft no form took soon after is stale. */
  quickAddDraftAt: number;
  setQuickAddDraft: (draft: QuickAddDraft) => void;
  clearQuickAddDraft: () => void;
}

/**
 * Every transient panel/dialog field, in one place so the store's initial state
 * and `resetPanels()` can never drift apart.
 */
const INITIAL_PANEL_STATE = {
  // Sidepanel (view details)
  selectedRecipient: null,
  sidepanelOpen: false,
  // Edit sidepanel
  editRecipient: null,
  editSidepanelOpen: false,
  // Add modal/sidepanel
  addOptionsModalOpen: false,
  addSidepanelOpen: false,
  // Export
  exportDialogOpen: false,
  selectedIdsForExport: [],
} satisfies Partial<RecipientsUIState>;

export const useRecipientsUIStore = create<RecipientsUIState>()(
  devtools(
    (set) => ({
      ...INITIAL_PANEL_STATE,
      openSidepanel: (recipient) => set({ selectedRecipient: recipient, sidepanelOpen: true }),
      closeSidepanel: () => set({ selectedRecipient: null, sidepanelOpen: false }),
      updateSelectedRecipient: (updates) =>
        set((state) => ({
          selectedRecipient: state.selectedRecipient
            ? ({ ...state.selectedRecipient, ...updates } as Recipient)
            : null,
        })),

      openEditSidepanel: (recipient) => set({ editRecipient: recipient, editSidepanelOpen: true }),
      closeEditSidepanel: () => set({ editRecipient: null, editSidepanelOpen: false }),

      setAddOptionsModalOpen: (open) => set({ addOptionsModalOpen: open }),
      setAddSidepanelOpen: (open) => set({ addSidepanelOpen: open }),

      openExportDialog: (ids) => set({ exportDialogOpen: true, selectedIdsForExport: ids }),
      closeExportDialog: () => set({ exportDialogOpen: false, selectedIdsForExport: [] }),

      resetPanels: () => set({ ...INITIAL_PANEL_STATE }),

      quickAddDraft: null,
      quickAddDraftAt: 0,
      setQuickAddDraft: (draft) => set({ quickAddDraft: draft, quickAddDraftAt: Date.now() }),
      clearQuickAddDraft: () => set({ quickAddDraft: null, quickAddDraftAt: 0 }),
    }),
    { name: 'RecipientsUIStore' },
  ),
);
